import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api, { fileUrl, BACKEND_URL } from '../lib/api';
import { Radio, Users, Volume2, VolumeX, LogOut, ThumbsUp, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import TipButton from '../components/TipButton';

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true`;

/**
 * Live listener room. Connects as WebRTC viewer to the broadcaster and plays
 * incoming audio (and video, if the DJ went live in video mode).
 */
export default function LiveRoom() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('connecting'); // connecting | live | ended | error
  const [error, setError] = useState(null);
  const [tally, setTally] = useState({ total: 0, you_voted: false });
  const [voting, setVoting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.9);
  const mediaRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);

  // Poll live tally so the "who's winning" count stays fresh across viewers.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get(`/live/${streamId}/tally`)
        .then(r => { if (!cancelled) setTally(r.data); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [streamId]);

  const castVote = async () => {
    if (voting) return;
    setVoting(true);
    try {
      const next = !tally.you_voted;
      const r = await api.post(`/live/${streamId}/vote`, { thumbs: next });
      setTally(r.data);
      toast.success(next ? 'Vote locked in — this DJ is winning!' : 'Vote removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not vote');
    } finally {
      setVoting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const wsProto = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = `${wsProto}://${BACKEND_URL.replace(/^https?:\/\//, '')}/api/ws/live/${streamId}?role=viewer`;

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    pcRef.current = pc;

    pc.ontrack = (e) => {
      if (mediaRef.current && e.streams[0]) {
        mediaRef.current.srcObject = e.streams[0];
        mediaRef.current.play().catch(() => {});
      }
    };

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => { /* wait for viewer-id */ };
    ws.onmessage = async (event) => {
      const msg = JSON.parse(event.data);
      if (cancelled) return;
      if (msg.type === 'viewer-id') {
        setMeta(msg.meta);
        setStatus('live');
      } else if (msg.type === 'offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        ws.send(JSON.stringify({ type: 'answer', sdp: answer }));
      } else if (msg.type === 'ice' && msg.candidate) {
        try { await pc.addIceCandidate(new RTCIceCandidate(msg.candidate)); } catch {}
      } else if (msg.type === 'stream-ended' || msg.type === 'broadcaster-left') {
        setStatus('ended');
        try { ws.close(); } catch {}
      } else if (msg.type === 'error') {
        setError(msg.error || 'Connection error');
        setStatus('error');
      }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate && ws.readyState === 1) {
        ws.send(JSON.stringify({ type: 'ice', candidate: e.candidate }));
      }
    };
    ws.onerror = () => { setStatus('error'); setError('Signaling error'); };
    ws.onclose = () => { if (status !== 'ended' && !cancelled) setStatus((s) => s === 'live' ? 'ended' : s); };

    return () => {
      cancelled = true;
      try { pc.close(); } catch {}
      try { ws.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const leave = () => {
    try { pcRef.current?.close(); } catch {}
    try { wsRef.current?.close(); } catch {}
    navigate('/discover');
  };

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#EF4444] mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" />
            LIVE ON RAW MIXES
          </div>
          <h1 className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter">
            {meta?.title || 'Loading live set…'}
          </h1>
        </div>
        <button
          onClick={leave}
          className="btn-pill btn-outline flex items-center gap-2 text-sm"
          data-testid="live-leave-btn"
        >
          <LogOut size={14} /> Leave
        </button>
      </div>

      <div className="post-card p-6" data-testid="live-room">
        {status === 'connecting' && (
          <div className="text-[#A1A1AA] text-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FFB000] animate-pulse" /> Connecting to the live decks…
          </div>
        )}

        {status === 'error' && (
          <div className="text-[#EF4444] text-sm">{error || 'Could not connect.'}</div>
        )}

        {status === 'ended' && (
          <div className="space-y-3">
            <div className="text-white font-heading font-bold text-lg">The DJ has ended the stream.</div>
            <div className="text-[#A1A1AA] text-sm">Follow this DJ to catch the next one, or head back to Discover for more live sets.</div>
            <Link to="/discover" className="btn-pill btn-primary inline-flex items-center gap-2 mt-2" data-testid="live-ended-discover">
              Back to Discover
            </Link>
          </div>
        )}

        {(status === 'live' || status === 'connecting') && meta && (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <Link to={`/profile/${meta.user_id}`} className="flex items-center gap-3 group">
                <img
                  src={meta.avatar_path ? fileUrl(meta.avatar_path) : avatarFallback(meta.name)}
                  alt=""
                  className="w-14 h-14 rounded-full object-cover border border-[#FFB000]/40"
                />
                <div>
                  <div className="text-white font-heading font-bold group-hover:text-[#FFB000] transition-colors flex items-center gap-2">
                    {meta.name}
                    {meta.is_battle && (
                      <span className="px-2 py-0.5 rounded-full bg-[#FFB000] text-black text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                        <Flame size={10} /> Battle
                      </span>
                    )}
                  </div>
                  <div className="text-[#A1A1AA] text-xs">@{meta.username}</div>
                </div>
              </Link>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={castVote}
                  disabled={voting}
                  data-testid="live-vote-btn"
                  className={`btn-pill flex items-center gap-2 text-sm ${tally.you_voted ? 'btn-primary' : 'btn-outline'} ${voting ? 'opacity-60' : ''}`}
                >
                  <ThumbsUp size={14} />
                  <span data-testid="live-vote-count">{tally.total}</span>
                  <span className="hidden sm:inline">{tally.you_voted ? 'Voted' : 'Vote'}</span>
                </button>
                <TipButton recipient={{ user_id: meta.user_id, name: meta.name, username: meta.username }} variant="primary" />
              </div>
            </div>

            {meta.mode === 'video' ? (
              <video
                ref={mediaRef}
                autoPlay
                playsInline
                controls
                className="w-full rounded-xl bg-black border border-[#FFB000]/20"
                data-testid="live-video"
              />
            ) : (
              <>
                <audio ref={mediaRef} autoPlay data-testid="live-audio" style={{ display: 'none' }} />
                <div className="flex items-center gap-3 rounded-xl border border-[#FFB000]/20 bg-black/40 px-4 py-3" data-testid="live-audio-controls">
                  <button
                    type="button"
                    onClick={() => {
                      const el = mediaRef.current;
                      if (!el) return;
                      const next = !muted;
                      el.muted = next;
                      setMuted(next);
                    }}
                    className="w-10 h-10 rounded-full bg-[#FFB000]/10 hover:bg-[#FFB000]/20 border border-[#FFB000]/40 text-[#FFB000] flex items-center justify-center transition-colors"
                    data-testid="live-audio-mute"
                    aria-label={muted ? 'Unmute' : 'Mute'}
                  >
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={muted ? 0 : volume}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setVolume(v);
                      setMuted(v === 0);
                      if (mediaRef.current) {
                        mediaRef.current.volume = v;
                        mediaRef.current.muted = v === 0;
                      }
                    }}
                    className="flex-1 accent-[#FFB000]"
                    data-testid="live-audio-volume"
                    aria-label="Volume"
                  />
                  <div className="text-[10px] uppercase tracking-widest font-mono text-[#A1A1AA]">
                    {Math.round((muted ? 0 : volume) * 100)}%
                  </div>
                </div>
                <motion.div
                  className="mt-6 mx-auto w-56 h-56 rounded-full bg-black border-4 border-[#FFB000]/40 shadow-[0_0_40px_rgba(255,176,0,0.2)] flex items-center justify-center overflow-hidden"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                >
                  <div className="absolute inset-6 rounded-full border border-[#FFB000]/20" />
                  <div className="w-14 h-14 rounded-full bg-[#FFB000] flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-black" />
                  </div>
                </motion.div>
              </>
            )}

            <div className="flex items-center gap-4 mt-4 text-xs text-[#A1A1AA] font-mono uppercase tracking-widest">
              <div className="flex items-center gap-1.5"><Radio size={12} className="text-[#EF4444]" /> Live</div>
              <div className="flex items-center gap-1.5"><Volume2 size={12} /> {meta.mode === 'video' ? 'A/V' : 'Audio'}</div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
